// lib/qqq/narratives.js
// ═══════════════════════════════════════════════════════════
// QQQ SESSION BRIEF — deterministic conclusion narratives
// ═══════════════════════════════════════════════════════════
// Generates the 5 section "conclusion chip" paragraphs (the
// rounded text card at the top of each section that tells the
// user what the data means in plain language).
//
// Pure JS (no JSX) so the API endpoint can call this server-side
// and the client just renders the returned strings.
//
// Output shape per section:
//   {
//     text:   'string with **bold** segments and <up>up</up> /
//              <dn>down</dn> color spans',
//     badge:      'BULL' | 'BEAR' | 'WATCH' | 'MIXED' | 'CLEAN' | 'OVERRIDE' | 'WAIT',
//     badgeTone:  'bull' | 'bear' | 'neutral',
//     dotTone:    'bull' | 'bear' | 'neutral' | 'purple' | 'teal' | 'gold',
//   }
//
// Phase 3 can swap the templates for a Sonnet generator using
// user context. For now, deterministic templates cover the 80%
// case at zero latency / zero cost.
// ═══════════════════════════════════════════════════════════

import { scoreSignals } from './synthesis';

export function buildNarratives(sessionData) {
  const { signals } = scoreSignals(sessionData);
  const sigByKey = {};
  for (const s of signals) sigByKey[s.key] = s;

  return {
    price:       buildPriceNarrative(sessionData, sigByKey),
    environment: buildEnvironmentNarrative(sessionData, sigByKey),
    structure:   buildStructureNarrative(sessionData, sigByKey),
    flow:        buildFlowNarrative(sessionData, sigByKey),
    risk:        buildRiskNarrative(sessionData, sigByKey),
  };
}

// ─── Section 1: Where Are We (price + levels) ───
function buildPriceNarrative(sd, _sigs) {
  // Commit 26: read from BOTH _raw (when present) AND the public
  // levels block (always present — same source the levels strip
  // already uses successfully). This eliminates the fragile
  // dependency on the API stripRaw behavior — narrative now
  // works whether or not _raw makes it through.
  const raw = sd.prices?._raw || {};
  const lvl = sd.prices?.levels || {};
  const qqq = sd.prices?.qqq || {};
  const qqqPrice = num(raw.qqqPrice) ?? parseLooseNum(qqq.price) ?? parseLooseNum(lvl.qqqNow);
  const pdh = num(raw.qqqS?.prev_high) ?? parseLooseNum(lvl.pdh);
  const pdl = num(raw.qqqS?.prev_low)  ?? parseLooseNum(lvl.pdl);

  if (qqqPrice == null || pdh == null) {
    return { text: 'Price levels loading…', badge: 'WAIT', badgeTone: 'neutral', dotTone: 'neutral' };
  }
  if (qqqPrice > pdh) {
    const distance = (qqqPrice - pdh).toFixed(2);
    return {
      text: `**QQQ is above yesterday's high (PDH ${pdh.toFixed(2)})** by $${distance}. Breakout continuation signal is active.`,
      badge: 'BULL', badgeTone: 'bull', dotTone: 'bull',
    };
  }
  if (pdl != null && qqqPrice < pdl) {
    const distance = (pdl - qqqPrice).toFixed(2);
    return {
      text: `**QQQ is below yesterday's low (PDL ${pdl.toFixed(2)})** by $${distance}. Breakdown continuation signal is active.`,
      badge: 'BEAR', badgeTone: 'bear', dotTone: 'bear',
    };
  }
  return {
    text: `**QQQ is consolidating inside yesterday's range** (${pdl != null ? pdl.toFixed(2) : '—'} to ${pdh.toFixed(2)}). Direction-neutral until a side breaks.`,
    badge: 'WATCH', badgeTone: 'neutral', dotTone: 'neutral',
  };
}

// ─── Section 2: The Weather Today (environment) ───
function buildEnvironmentNarrative(sd, sigs) {
  const raw = sd.environment?._raw || {};
  const vix = num(raw.vix);
  const vix9d = num(raw.vix9d);
  const dxyChg = num(raw.dxyChangePct);
  const spread = num(raw.spread10y2y);

  const vixSig = sigs.vix_term;
  const dxySig = sigs.dollar;
  const ycSig  = sigs.yield;
  const bullCount = [vixSig, dxySig, ycSig].filter((s) => s?.tone === 'bull').length;
  const bearCount = [vixSig, dxySig, ycSig].filter((s) => s?.tone === 'bear').length;

  let backdrop;
  if (vix == null) backdrop = 'Backdrop loading';
  else if (vix < 15) backdrop = 'Calm backdrop';
  else if (vix < 20) backdrop = 'Normal volatility';
  else if (vix < 25) backdrop = 'Elevated volatility';
  else backdrop = 'Stressed regime';

  let termText = '';
  if (vix9d != null && vix != null) {
    if (vix9d < vix - 0.3) termText = ' with <up>contango term structure</up> — near-term fear is resolving.';
    else if (vix9d > vix + 0.3) termText = ' in <dn>backwardation</dn> — near-term fear spiking.';
    else termText = ' with flat term structure.';
  }

  let dxyText = '';
  if (dxyChg != null) {
    if (dxyChg < -0.15)      dxyText = ' Dollar weak.';
    else if (dxyChg > 0.15)  dxyText = ' Dollar strong.';
  }

  let yieldText = '';
  if (spread != null && spread < 0) yieldText = ` Yield curve inverted (${spread.toFixed(2)}pp) — long-term caution.`;
  else if (spread != null && spread > 0.5) yieldText = ' Yield curve healthy.';

  const text = `**${backdrop}.** VIX at ${vix != null ? vix.toFixed(1) : '—'}${termText}${dxyText}${yieldText}`;

  let badge, badgeTone, dotTone;
  if (bullCount > bearCount && bullCount >= 2) { badge = 'BULL'; badgeTone = 'bull'; dotTone = 'purple'; }
  else if (bearCount > bullCount && bearCount >= 2) { badge = 'BEAR'; badgeTone = 'bear'; dotTone = 'bear'; }
  else { badge = 'MIXED'; badgeTone = 'neutral'; dotTone = 'purple'; }

  return { text, badge, badgeTone, dotTone };
}

// ─── Section 3: What Dealers Must Do (structure) ───
function buildStructureNarrative(sd, _sigs) {
  const raw = sd.structure?._raw || {};
  const above = num(raw.aboveFlip);
  const flip = num(raw.gammaFlip);
  const cw   = num(raw.callWall);
  const pw   = num(raw.putWall);

  if (flip == null || above == null) {
    return { text: 'Gamma structure loading…', badge: 'WAIT', badgeTone: 'neutral', dotTone: 'teal' };
  }
  if (above > 0.5) {
    return {
      text: `**Positive gamma regime.** QQQ is ${Math.abs(above).toFixed(2)} points above the flip at ${flip.toFixed(2)}. Dealers are <up>buying dips and selling rips</up> — price will be mechanically suppressed today.${cw != null && pw != null ? ` Range: ${pw.toFixed(0)}–${cw.toFixed(0)}.` : ''}`,
      badge: 'BULL', badgeTone: 'bull', dotTone: 'teal',
    };
  }
  if (above < -0.5) {
    return {
      text: `**Negative gamma regime.** QQQ is ${Math.abs(above).toFixed(2)} points below the flip at ${flip.toFixed(2)}. Dealers are <dn>selling dips and buying rips</dn> — moves get amplified, expect expansion.${pw != null ? ` Watch put wall ${pw.toFixed(0)} as the magnet.` : ''}`,
      badge: 'BEAR', badgeTone: 'bear', dotTone: 'teal',
    };
  }
  return {
    text: `**Near gamma flip (${flip.toFixed(2)}).** Regime indeterminate — dealer hedging could swing either way. Wait for resolution before sizing.`,
    badge: 'WATCH', badgeTone: 'neutral', dotTone: 'teal',
  };
}

// ─── Section 4: Where Is Money Going (flow) ───
function buildFlowNarrative(sd, sigs) {
  const raw = sd.flow?._raw || {};
  const pcrVal = num(raw.pcrValue);
  const techLead = num(raw.techLeadingSpy);
  const breadthPct = num(sd.environment?._raw?.breadthPct);
  const pcrSig = sigs.pcr;
  const techSig = sigs.tech;

  let pcrPhrase;
  if (pcrVal == null) pcrPhrase = 'PCR loading';
  else if (pcrVal < 0.85) pcrPhrase = `PCR ${pcrVal.toFixed(2)} confirms call buying`;
  else if (pcrVal > 1.05) pcrPhrase = `PCR ${pcrVal.toFixed(2)} shows put-heavy positioning`;
  else                    pcrPhrase = `PCR ${pcrVal.toFixed(2)} — neutral options flow`;

  let techPhrase = '';
  if (techLead != null) {
    if (techLead > 0.15)       techPhrase = ` <up>Tech leading SPY by +${techLead.toFixed(2)}pp</up> — money rotating into QQQ components.`;
    else if (techLead < -0.15) techPhrase = ` <dn>Tech lagging SPY by ${techLead.toFixed(2)}pp</dn> — rotation away from growth.`;
    else                       techPhrase = ' Tech moving with broad market.';
  }

  let breadthPhrase = '';
  if (breadthPct != null) {
    if (breadthPct >= 70)      breadthPhrase = ` Top-10 breadth strong (${breadthPct}% positive).`;
    else if (breadthPct <= 30) breadthPhrase = ` Top-10 breadth narrow (${breadthPct}% positive) — fragile.`;
  }

  let headline;
  if (pcrSig?.tone === 'bull' && techSig?.tone === 'bull') headline = 'Money flowing into calls and tech.';
  else if (pcrSig?.tone === 'bear' && techSig?.tone === 'bear') headline = 'Money flowing out of calls and tech.';
  else headline = 'Mixed flow signals.';

  const text = `**${headline}** ${pcrPhrase}.${techPhrase}${breadthPhrase}`;

  const bullCount = [pcrSig, techSig].filter((s) => s?.tone === 'bull').length;
  const bearCount = [pcrSig, techSig].filter((s) => s?.tone === 'bear').length;
  let badge, badgeTone, dotTone;
  if (bullCount > bearCount) { badge = 'BULL'; badgeTone = 'bull'; dotTone = 'bull'; }
  else if (bearCount > bullCount) { badge = 'BEAR'; badgeTone = 'bear'; dotTone = 'bear'; }
  else { badge = 'MIXED'; badgeTone = 'neutral'; dotTone = 'neutral'; }

  return { text, badge, badgeTone, dotTone };
}

// ─── Section 5: What Could Override Everything (risk) ───
function buildRiskNarrative(sd, _sigs) {
  const events = Array.isArray(sd.risk) ? sd.risk : [];
  const inWindow   = events.filter((e) => e.sev === 'hi');
  const inWindowMd = events.filter((e) => e.sev === 'md');

  if (inWindow.length > 0) {
    const first = inWindow[0];
    return {
      text: `<warn>**High-impact event in window: ${first.name}${first.time ? ` at ${first.time}` : ''}.**</warn> Verdict can flip mid-session — reduce size or skip until 11:30.`,
      badge: 'OVERRIDE', badgeTone: 'bear', dotTone: 'bear',
    };
  }
  if (inWindowMd.length > 0) {
    const first = inWindowMd[0];
    return {
      text: `**Window is mostly clean.** ${first.name}${first.time ? ` at ${first.time}` : ''} is medium impact — watch but do not pause entries for this.`,
      badge: 'WATCH', badgeTone: 'neutral', dotTone: 'neutral',
    };
  }
  return {
    text: `**Window is clean 9:30–11:30.** No high-impact events scheduled. Trade the verdict cleanly through the window.`,
    badge: 'CLEAN', badgeTone: 'bull', dotTone: 'bull',
  };
}

// ─── Sub-card footer helpers ───

export function vixTermPill(sd) {
  const raw = sd.environment?._raw || {};
  const vix9 = num(raw.vix9d);
  const vix  = num(raw.vix);
  if (vix9 == null || vix == null) return { text: 'Term structure loading', tone: 'neutral' };
  if (vix9 < vix - 0.3) {
    return { text: `Contango · 9D (${vix9.toFixed(1)}) below 30D (${vix.toFixed(1)}) · Fear resolving`, tone: 'bull' };
  }
  if (vix9 > vix + 0.3) {
    return { text: `Backwardation · 9D (${vix9.toFixed(1)}) above 30D (${vix.toFixed(1)}) · Acute near-term risk`, tone: 'bear' };
  }
  return { text: `Flat term structure · 9D (${vix9.toFixed(1)}) ≈ 30D (${vix.toFixed(1)})`, tone: 'neutral' };
}

export function yieldSpreadFooter(sd) {
  const spread = num(sd.environment?._raw?.spread10y2y);
  if (spread == null) return { text: 'Spread loading', tone: 'neutral' };
  if (spread < -0.25) return { text: `${spread.toFixed(2)}% · Deeply inverted · Recession watch`, tone: 'bear' };
  if (spread < 0)     return { text: `${spread.toFixed(2)}% · Inverted · Long-term caution`, tone: 'bear' };
  if (spread < 0.5)   return { text: `+${spread.toFixed(2)}% · Flat · Transition zone`, tone: 'neutral' };
  return { text: `+${spread.toFixed(2)}% · Healthy curve · Growth-supportive`, tone: 'bull' };
}

export function sectorLeadershipFooter(sd) {
  const techLead = num(sd.flow?._raw?.techLeadingSpy);
  if (techLead == null) return { text: 'Sector data loading', tone: 'neutral' };
  if (techLead > 0.15) {
    return { text: `Tech leading SPY by +${techLead.toFixed(2)}pp · QQQ has sector tailwind`, tone: 'bull' };
  }
  if (techLead < -0.15) {
    return { text: `Tech lagging SPY by ${techLead.toFixed(2)}pp · QQQ has sector headwind`, tone: 'bear' };
  }
  return { text: `Tech moving with broad market (${techLead >= 0 ? '+' : ''}${techLead.toFixed(2)}pp)`, tone: 'neutral' };
}

// ─── helpers ───
function num(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
// parseLooseNum: tolerates strings like "655.33", "—", null, etc.
// Used as a fallback when _raw is missing and we need to extract
// a numeric value from the formatted public block.
function parseLooseNum(v) {
  if (v == null || v === '—') return null;
  const cleaned = String(v).replace(/[^0-9.\-]/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}
